import { getSchemaMax, getSchemaMin, SchemaControlLike } from './schema-min.util';

/**
 * Resolved numeric behaviour for one schema control — the single answer to
 * "how does this field render and sanitise?".
 */
export interface NumericFieldPolicy {
  /** Fraction digits shown (both min and max on `p-inputnumber`). */
  decimals: number;
  /** Thousand separators. */
  grouping: boolean;
  /** Lower bound, or `undefined` when unconstrained. */
  min?: number;
  /** Upper bound, or `undefined` when unconstrained. */
  max?: number;
  /** Digits allowed BEFORE the decimal point, excluding the sign. */
  maxIntegerDigits: number;
  /** Whether decimals are coerced away and the sign stripped. */
  integerOnly: boolean;
}

/** Types whose XBRL semantics are whole-number and non-negative. */
const INTEGER_TYPES = new Set<string>([
  'IntegerItemType',
  'NonNegativeInteger',
  'NonNegativeIntegerItemType',
]);

/**
 * Total digits a filing value may carry, fraction included. The fraction
 * allowance DEDUCTS from the integer allowance: `integerDigits = 15 - decimals`,
 * so a whole-number field takes 15 integer digits and a 2-decimal field takes
 * 13 + 2.
 *
 * Provenance (PO, verified against the Govt. MBRS Prep Tool, 2026-08-11): the
 * Prep Tool cannot validate values at >= 16 digits on a whole number, or at
 * >= 14 integer digits when 2 decimals are present. This is an external
 * interop ceiling, NOT an XBRL taxonomy rule — which is why no field record in
 * `docs/specifications/sheets/` declares it and the global rule lives in
 * `_sheet-notes.md` instead.
 *
 * The ceiling is on the INTEGER part, never the total: a flat 15-digit total
 * check would wrongly accept 14 integer digits alongside 1 decimal.
 */
export const MAX_TOTAL_DIGITS = 15;

/** Fraction digits for a signed monetary/per-share field with no override. */
const DEFAULT_DECIMALS = 2;

/**
 * Derive a control's numeric policy.
 *
 * Precedence: `control.type` supplies the default, `ui.*` overrides it, and
 * `validators[]` bounds are read through the existing `schema-min.util`
 * readers so there is still exactly one answer to "what is this control's
 * min?" across the mounted and headless paths.
 */
export function resolveNumericPolicy(control: SchemaControlLike | null | undefined): NumericFieldPolicy {
  const ui = control?.ui ?? {};
  const type = control?.type;

  const integerOnly = (type ? INTEGER_TYPES.has(type) : false) || ui.positiveWholeNumber === true;

  const decimals = typeof ui.decimalPlaces === 'number'
    ? ui.decimalPlaces
    : (integerOnly ? 0 : DEFAULT_DECIMALS);

  const grouping = typeof ui.useGrouping === 'boolean' ? ui.useGrouping : true;

  const maxIntegerDigits = typeof ui.maxIntegerDigits === 'number'
    ? ui.maxIntegerDigits
    : Math.max(0, MAX_TOTAL_DIGITS - decimals);

  const min = getSchemaMin(control);

  return {
    decimals,
    grouping,
    min,
    max: getSchemaMax(control),
    maxIntegerDigits,
    integerOnly,
  };
}
